<template>
  <section class="mx-auto max-w-7xl px-6 py-10">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Tournament Room</p>
        <h1 class="mt-2 font-display text-3xl font-semibold text-white">{{ room?.room_name || 'Room' }}</h1>
        <p class="mt-2 text-sm text-steel-300">{{ roomStatus }}</p>
      </div>
      <router-link
        to="/tournament"
        class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
      >
        Back to rooms
      </router-link>
    </div>

    <p v-if="statusMessage" class="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{{ statusMessage }}</p>
    <p v-if="errorMessage" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{{ errorMessage }}</p>

    <div class="mt-8 grid gap-6 lg:grid-cols-[1fr,auto,1fr] lg:items-center">
      <div class="rounded-[2rem] border border-white/10 bg-midnight-900/60 p-5">
        <template v-if="leftEntry">
          <div class="overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
            <img v-if="leftEntry.photo" :src="leftEntry.photo" :alt="leftEntry.carName" class="aspect-[4/3] w-full object-cover" :style="{ objectPosition: leftEntry.photoPosition }" />
            <div v-else class="flex aspect-[4/3] items-center justify-center text-sm uppercase tracking-[0.3em] text-steel-400">No photo</div>
          </div>
          <div class="mt-5 flex items-center gap-3">
            <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img v-if="leftEntry.avatar" :src="leftEntry.avatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ leftEntry.initials }}</div>
            </div>
            <div>
              <p class="text-base font-semibold text-white">{{ leftEntry.displayName }}</p>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ leftEntry.handle }}</p>
            </div>
          </div>
          <div class="mt-5">
            <p class="text-2xl font-semibold text-white">{{ leftEntry.carName }}</p>
            <p class="mt-3 whitespace-pre-line text-sm leading-6 text-steel-300">{{ leftEntry.specs }}</p>
          </div>
          <div class="mt-6 flex items-center justify-between gap-3">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ leftEntry.votes }} votes</p>
            <button type="button" class="rounded-2xl px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] transition" :class="voteButtonClass(leftEntry)" :disabled="!canVote(leftEntry)" @click="castVote(leftEntry.id)">
              {{ voteButtonLabel(leftEntry) }}
            </button>
          </div>
        </template>
        <template v-else>
          <div class="flex min-h-[28rem] items-center justify-center rounded-[1.5rem] border border-dashed border-white/10 bg-white/[0.03] p-8 text-center text-sm text-steel-400">
            Slot 1 is still waiting for a build.
          </div>
        </template>
      </div>

      <div class="flex items-center justify-center">
        <div class="rounded-full border border-white/10 bg-white/5 px-6 py-5 text-center">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Face Off</p>
          <p class="mt-2 font-display text-4xl font-semibold text-white">VS</p>
        </div>
      </div>

      <div class="rounded-[2rem] border border-white/10 bg-midnight-900/60 p-5">
        <template v-if="rightEntry">
          <div class="overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
            <img v-if="rightEntry.photo" :src="rightEntry.photo" :alt="rightEntry.carName" class="aspect-[4/3] w-full object-cover" :style="{ objectPosition: rightEntry.photoPosition }" />
            <div v-else class="flex aspect-[4/3] items-center justify-center text-sm uppercase tracking-[0.3em] text-steel-400">No photo</div>
          </div>
          <div class="mt-5 flex items-center gap-3">
            <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img v-if="rightEntry.avatar" :src="rightEntry.avatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ rightEntry.initials }}</div>
            </div>
            <div>
              <p class="text-base font-semibold text-white">{{ rightEntry.displayName }}</p>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ rightEntry.handle }}</p>
            </div>
          </div>
          <div class="mt-5">
            <p class="text-2xl font-semibold text-white">{{ rightEntry.carName }}</p>
            <p class="mt-3 whitespace-pre-line text-sm leading-6 text-steel-300">{{ rightEntry.specs }}</p>
          </div>
          <div class="mt-6 flex items-center justify-between gap-3">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ rightEntry.votes }} votes</p>
            <button type="button" class="rounded-2xl px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] transition" :class="voteButtonClass(rightEntry)" :disabled="!canVote(rightEntry)" @click="castVote(rightEntry.id)">
              {{ voteButtonLabel(rightEntry) }}
            </button>
          </div>
        </template>
        <template v-else>
          <div class="flex min-h-[28rem] items-center justify-center rounded-[1.5rem] border border-dashed border-white/10 bg-white/[0.03] p-8 text-center text-sm text-steel-400">
            Slot 2 is still waiting for a build.
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'

const ACTIVE_BRACKET_KEY = 'main'
const VOTE_TARGET = 5

export default {
  name: 'TournamentRoomDetailView',
  data() {
    return {
      room: null,
      entries: [],
      votes: [],
      sessionUser: null,
      errorMessage: '',
      statusMessage: '',
      liveChannel: null,
      authSubscription: null,
    }
  },
  computed: {
    leftEntry() {
      return this.entries.find((entry) => entry.slotNumber === 1) || null
    },
    rightEntry() {
      return this.entries.find((entry) => entry.slotNumber === 2) || null
    },
    currentUserVote() {
      const userId = this.sessionUser?.id
      if (!userId || !this.room?.id) return ''
      return this.votes.find((vote) => vote.room_id === this.room.id && vote.voter_id === userId)?.entry_id || ''
    },
    roomStatus() {
      if (!this.room) return 'Loading room...'
      if (this.room.winner_entry_id) {
        const winner = this.entries.find((entry) => entry.id === this.room.winner_entry_id)
        return winner ? `${winner.carName} already advanced from this room.` : 'Winner already decided.'
      }
      if (this.leftEntry && this.rightEntry) return `Voting live. First to ${VOTE_TARGET} votes advances.`
      return 'Waiting for both slots to be filled.'
    },
  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    this.sessionUser = data?.session?.user || null
    await this.fetchRoom()
    this.setupRealtime()

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      this.sessionUser = session?.user || null
      await this.fetchRoom()
    })
    this.authSubscription = authListener?.subscription || null
  },
  beforeUnmount() {
    if (this.liveChannel) supabase.removeChannel(this.liveChannel)
    if (this.authSubscription) this.authSubscription.unsubscribe()
  },
  methods: {
    async fetchRoom() {
      this.errorMessage = ''
      const roomId = this.$route.params.id

      const { data: room, error: roomError } = await supabase
        .from('tournament_rooms')
        .select('id, bracket_key, room_name, winner_entry_id, status')
        .eq('id', roomId)
        .eq('bracket_key', ACTIVE_BRACKET_KEY)
        .maybeSingle()

      if (roomError || !room) {
        this.errorMessage = roomError?.message || 'Room not found.'
        this.room = null
        this.entries = []
        this.votes = []
        return
      }

      this.room = room

      const [{ data: entries, error: entryError }, { data: votes, error: voteError }] = await Promise.all([
        supabase
          .from('tournament_entries')
          .select('id, user_id, slot_number, car_name, specs, photo_path, photo_url, photo_offset_x, photo_offset_y')
          .eq('room_id', room.id)
          .order('slot_number', { ascending: true }),
        supabase
          .from('tournament_votes')
          .select('id, room_id, entry_id, voter_id')
          .eq('room_id', room.id),
      ])

      if (entryError || voteError) {
        this.errorMessage = entryError?.message || voteError?.message || 'Unable to load room details.'
        return
      }

      const userIds = [...new Set((entries || []).map((entry) => entry.user_id).filter(Boolean))]
      let profilesById = new Map()
      if (userIds.length) {
        const { data: profiles } = await supabase.from('profiles').select('id, handle, full_name, avatar_url, avatar_path').in('id', userIds)
        profilesById = new Map((profiles || []).map((profile) => [profile.id, profile]))
      }

      const voteCounts = new Map()
      ;(votes || []).forEach((vote) => {
        voteCounts.set(vote.entry_id, (voteCounts.get(vote.entry_id) || 0) + 1)
      })

      this.entries = (entries || []).map((entry) => {
        const profile = profilesById.get(entry.user_id) || {}
        const fullName = profile.full_name || 'DRVN Driver'
        return {
          id: entry.id,
          userId: entry.user_id,
          slotNumber: entry.slot_number,
          displayName: fullName,
          handle: profile.handle || 'member',
          avatar: getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url }),
          initials: fullName.split(' ').filter(Boolean).slice(0, 2).map((chunk) => chunk[0].toUpperCase()).join('') || 'D',
          carName: entry.car_name,
          specs: entry.specs,
          photoPosition: `${Number.isFinite(entry.photo_offset_x) ? entry.photo_offset_x : 50}% ${Number.isFinite(entry.photo_offset_y) ? entry.photo_offset_y : 50}%`,
          photo: entry.photo_path ? supabase.storage.from('posts').getPublicUrl(entry.photo_path).data?.publicUrl || entry.photo_url || '' : entry.photo_url || '',
          votes: voteCounts.get(entry.id) || 0,
        }
      })
      this.votes = votes || []
    },
    setupRealtime() {
      if (this.liveChannel) supabase.removeChannel(this.liveChannel)
      this.liveChannel = supabase
        .channel(`tournament-room-${this.$route.params.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_rooms' }, () => this.fetchRoom())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_entries' }, () => this.fetchRoom())
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_votes' }, () => this.fetchRoom())
        .subscribe()
    },
    canVote(entry) {
      if (!entry || !this.sessionUser?.id) return false
      if (this.room?.winner_entry_id) return false
      if (!this.leftEntry || !this.rightEntry) return false
      if (entry.userId === this.sessionUser.id) return false
      return true
    },
    voteButtonClass(entry) {
      if (this.room?.winner_entry_id === entry.id) return 'bg-emerald-500 text-midnight-900'
      if (!this.canVote(entry)) return 'border border-white/10 bg-white/5 text-steel-400'
      return 'bg-white text-midnight-900 hover:bg-amber-300'
    },
    voteButtonLabel(entry) {
      if (this.room?.winner_entry_id === entry.id) return 'Advanced'
      if (!this.sessionUser?.id) return 'Login to vote'
      if (entry.userId === this.sessionUser.id) return 'Your build'
      if (!this.leftEntry || !this.rightEntry) return 'Waiting for opponent'
      if (this.currentUserVote === entry.id) return 'Your vote'
      if (this.currentUserVote) return 'Switch vote'
      return 'Vote'
    },
    async castVote(entryId) {
      this.errorMessage = ''
      this.statusMessage = ''
      if (!this.sessionUser?.id || !this.room?.id) {
        this.errorMessage = 'Please log in before voting.'
        return
      }
      const { data, error } = await supabase.rpc('cast_tournament_vote', {
        p_bracket_key: ACTIVE_BRACKET_KEY,
        p_room_id: this.room.id,
        p_entry_id: entryId,
      })
      if (error) {
        this.errorMessage = error.message || 'Unable to cast vote.'
        return
      }
      this.statusMessage = data?.[0]?.winner_entry_id ? 'Vote counted and winner advanced.' : 'Vote counted.'
      await this.fetchRoom()
    },
  },
}
</script>
